import { type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Paint order for the credit gate -> checkout -> payment flow.
 *
 * The modals in this flow are rendered from wherever the user triggered them
 * (the chat, the report, the sidebar, the page header), while the checkout and
 * payment modals are rendered once from `CheckoutProvider` at the app root.
 * Comparing z-index across those two trees only works while every ancestor in
 * between stays out of the way — one `transform`, `filter` or stray z-index on
 * a page wrapper creates a stacking context and the checkout ends up buried
 * under the modal that opened it. Rendering all of them through `ModalPortal`
 * puts them in the same parent (<body>), so these values are the whole story.
 *
 * Steps go up: whatever opens the next step must sit below it.
 */
export const MODAL_LAYER = {
  /** Credit gates and the top-up modal — the entry points into the flow. */
  gate: 10000,
  /** Checkout summary. */
  checkout: 10300,
  /** Paystack embed. */
  payment: 10400,
} as const;

export type ModalLayer = (typeof MODAL_LAYER)[keyof typeof MODAL_LAYER];

const ModalPortal = ({ children }: { children: ReactNode }) =>
  createPortal(children, document.body);

export { ModalPortal };
